// src/utils/reporteOrdenServicioGenerator.js
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { obtenerClientePorId } from '@/api/clientes.js';
// 👈 NUEVO: Importar función que obtiene los datos de la empresa actual
import { obtenerDatosEmpresa } from './tenantConfig';

// ==================== CONFIGURACIÓN (FALLBACK) ====================
const EMPRESA_CONFIG = {
  nombre: 'SYSTEM JJ',
  rif: 'J-41023119-0',
  direccion: 'C.C. LATIN CENTER PB LOCAL 01',
  ciudad: 'ACARIGUA - PORTUGUESA',
  telefono: '04125696219 / 04167594723',
  email: 'SOPORTE@SYSTEMJJ.COM',
  slogan: 'Servicio Técnico Especializado',
  instagram: '@systemjj'
};

/**
 * 👈 NUEVO: Obtiene la config de la empresa actual con fallback
 */
const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa();
    return {
      nombre: datos.nombreComercial || datos.nombre || EMPRESA_CONFIG.nombre,
      rif: datos.rif || EMPRESA_CONFIG.rif,
      direccion: datos.direccion || EMPRESA_CONFIG.direccion,
      ciudad: datos.ciudad || EMPRESA_CONFIG.ciudad,
      telefono: datos.telefono1
        ? `${datos.telefono1}${datos.telefono2 ? ' / ' + datos.telefono2 : ''}`
        : EMPRESA_CONFIG.telefono,
      email: datos.email || EMPRESA_CONFIG.email,
      slogan: datos.slogan || EMPRESA_CONFIG.slogan,
      instagram: datos.instagram || EMPRESA_CONFIG.instagram,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...EMPRESA_CONFIG, logoBase64: null };
  }
};

let LOGO_BASE64 = '';

// ==================== CARGAR LOGO ====================
/**
 * 👈 MODIFICADO: Prioriza el logo del tenant; si no hay, usa /jj.png
 */
export const cargarLogo = async () => {
  try {
    // Primero intentar con el logo del tenant
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      const base64Limpio = empresa.logoBase64.includes(',')
        ? empresa.logoBase64.split(',')[1]
        : empresa.logoBase64;
      LOGO_BASE64 = base64Limpio;
      console.log('✅ Logo del tenant cargado');
      return LOGO_BASE64;
    }

    // Fallback al logo por defecto
    const response = await fetch('/jj.png');
    if (!response.ok) throw new Error('Logo no encontrado');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = reader.result.split(',')[1];
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => {
        LOGO_BASE64 = '';
        resolve('');
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error cargando el logo:', error);
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoBase64 = () => LOGO_BASE64;

// ==================== UTILIDADES ====================
export const formatearFechaTicket = (fechaStr) => {
  if (!fechaStr) return '—';
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return fechaStr;
  }
};

export const formatearHoraTicket = (fechaStr) => {
  if (!fechaStr) return '—';
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleTimeString('es-VE', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return fechaStr;
  }
};

export const formatearMonedaUSD = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '$ 0.00';
  return `$ ${Number(valor).toFixed(2)}`;
};

export const formatearMonedaBs = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return 'Bs 0.00';
  return `Bs ${Number(valor).toFixed(2)}`;
};

export const formatearGarantiaMostrar = (dias) => {
  if (!dias || dias === 0) return '';
  if (dias >= 365) return `${Math.floor(dias / 365)} año${Math.floor(dias / 365) !== 1 ? 's' : ''}`;
  if (dias >= 30) return `${Math.floor(dias / 30)} mes${Math.floor(dias / 30) !== 1 ? 'es' : ''}`;
  return `${dias} día${dias !== 1 ? 's' : ''}`;
};

// ==================== OBTENER DATOS CLIENTE ====================
export const obtenerDatosClienteCompletos = async (orden) => {
  const idCliente = orden.idCliente || orden.IdCliente || orden.clienteId;
  
  let clienteData = null;
  
  if (idCliente) {
    try {
      const result = await obtenerClientePorId(idCliente);
      if (result.success && result.data) {
        clienteData = result.data;
      }
    } catch (error) {
      console.warn('⚠️ No se pudo obtener el cliente por ID:', error);
    }
  }
  
  if (clienteData) {
    return {
      nombre: `${clienteData.nombre || ''} ${clienteData.apellido || ''}`.trim().toUpperCase() || 'CONSUMIDOR FINAL',
      documento: clienteData.documento || '—',
      direccion: clienteData.direccion || '—',
      telefono: clienteData.telefono || '—'
    };
  }
  
  return {
    nombre: (orden.nombreCliente || orden.NombreCliente || 'CONSUMIDOR FINAL').toUpperCase(),
    documento: orden.documentoCliente || orden.DocumentoCliente || '—',
    direccion: orden.direccionCliente || orden.DireccionCliente || '—',
    telefono: orden.telefonoCliente || orden.TelefonoCliente || orden.telefono || '—'
  };
};

// ==================== GENERAR HTML ORDEN DE SERVICIO ====================
export const generarHTMLOrdenServicio = (orden, datosCliente) => {
  if (!orden) {
    return `<div style="padding:40px;text-align:center;">Orden no encontrada</div>`;
  }

  const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant
  const logoImg = getLogoBase64() ? `data:image/jpeg;base64,${getLogoBase64()}` : '';

  // Datos de la orden
  const numeroOrden = orden.numeroOrden || orden.NumeroOrden || '—';
  const fechaIngreso = orden.fechaIngreso || orden.FechaIngreso;
  
  const equipo = (orden.descripcion || orden.Descripcion || '—').toUpperCase();
  const problema = (orden.problema || orden.Problema || '—').toUpperCase();
  const accesorios = (orden.accesorios || orden.Accesorios || 'SIN ACCESORIOS').toUpperCase();
  const sede = orden.nombreSede || orden.NombreSede || orden.taller || orden.Taller || 'Local 01';
  const diagnostico = orden.tipoDiagnostico || orden.TipoDiagnostico || 'GRATIS';
  
  // Servicios y productos
  let servicios = orden.servicios || [];
  let productos = orden.productos || [];
  
  // Si no hay servicios/productos en la orden, buscar en detalles
  if (servicios.length === 0 && productos.length === 0 && orden.detalles) {
    const detalles = orden.detalles || [];
    servicios = detalles
      .filter(d => d.idServicio || d.idServicio > 0 || d.tipoItem === 'servicio' || (!d.idProducto && d.observaciones))
      .map(d => ({
        nombre: d.nombreServicio || d.observaciones || 'Servicio',
        precio: d.precioUnitarioUsd || d.subtotalUsd || 0,
        cantidad: d.cantidad || 1,
        garantia: d.garantiaDias || 0,
        garantiaDias: d.garantiaDias || 0
      }));
    
    productos = detalles
      .filter(d => d.idProducto || d.idProducto > 0 || d.tipoItem === 'producto')
      .map(d => ({
        nombre: d.nombreProducto || d.nombre || 'Producto',
        precio: d.precioUnitarioUsd || d.subtotalUsd || 0,
        cantidad: d.cantidad || 1,
        garantia: d.garantiaDias || 0,
        garantiaDias: d.garantiaDias || 0
      }));
  }
  
  // Si aún no hay servicios, buscar en recepcionEquipoDetalles
  if (servicios.length === 0 && productos.length === 0 && orden.recepcionEquipoDetalles) {
    const detalles = orden.recepcionEquipoDetalles || [];
    servicios = detalles
      .filter(d => d.idServicio || d.idServicio > 0 || (!d.idProducto && d.observaciones))
      .map(d => ({
        nombre: d.nombreServicio || d.observaciones || 'Servicio',
        precio: d.precioUnitarioUsd || d.subtotalUsd || 0,
        cantidad: d.cantidad || 1,
        garantia: d.garantiaDias || 0,
        garantiaDias: d.garantiaDias || 0
      }));
    
    productos = detalles
      .filter(d => d.idProducto || d.idProducto > 0)
      .map(d => ({
        nombre: d.nombreProducto || d.nombre || 'Producto',
        precio: d.precioUnitarioUsd || d.subtotalUsd || 0,
        cantidad: d.cantidad || 1,
        garantia: d.garantiaDias || 0,
        garantiaDias: d.garantiaDias || 0
      }));
  }

  // Totales
  const precioTotal = Number(orden.precioTotalUsd || orden.PrecioTotalUsd || 0);
  const descuento = Number(orden.descuento || orden.Descuento || 0);
  const montoPagadoRep = Number(orden.montoPagadoReparacionUsd || orden.MontoPagadoReparacionUsd || 0);
  const tasaBsUsd = Number(orden.tasaBsUsd || orden.TasaBsUsd || 0);
  
  const totalFinal = precioTotal - descuento;
  const totalServicios = servicios.reduce((sum, s) => sum + (s.precio || 0), 0);
  const totalProductos = productos.reduce((sum, p) => sum + (p.precio || 0), 0);
  const subtotalCalculado = totalServicios + totalProductos;
  
  const totalMostrar = subtotalCalculado > 0 ? subtotalCalculado : precioTotal;
  const totalBs = Number((totalMostrar * tasaBsUsd).toFixed(2));
  const montoPagadoBs = Number((montoPagadoRep * tasaBsUsd).toFixed(2));

  const cliente = datosCliente || {
    nombre: 'CONSUMIDOR FINAL',
    telefono: '—'
  };

  // Logo HTML
  const logoHTML = logoImg ? `<img src="${logoImg}" style="height:80px;width:auto;object-fit:contain;" onerror="this.style.display='none'">` : '';

  return `
    <div style="width:700px;margin:0 auto;padding:12px 15px;font-family:'Courier New',monospace;font-size:10px;color:#000;background:white;">
      
      <!-- HEADER con logos a izquierda y derecha -->
      <div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:4px;margin-bottom:4px;border-bottom:2px solid #000;">
        <div style="display:flex;align-items:center;width:90px;justify-content:center;">
          ${logoHTML || '<div style="width:80px;height:80px;"></div>'}
        </div>
        <div style="text-align:center;flex:1;">
          <div style="font-size:16px;font-weight:bold;">${empresa.nombre}</div>
          <div style="font-size:9px;">${empresa.direccion}</div>
          <div style="font-size:9px;">${empresa.ciudad}</div>
          <div style="font-size:9px;">TEL: ${empresa.telefono}</div>
          <div style="font-size:9px;font-weight:bold;">RIF: ${empresa.rif}</div>
        </div>
        <div style="display:flex;align-items:center;width:90px;justify-content:center;">
          ${logoHTML || '<div style="width:80px;height:80px;"></div>'}
        </div>
      </div>

      <!-- TÍTULO -->
      <div style="display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px solid #ddd;margin-bottom:3px;">
        <div style="font-size:13px;font-weight:bold;">ORDEN DE SERVICIO No. : ${numeroOrden}</div>
        <div style="font-size:8px;">Fecha: ${formatearFechaTicket(fechaIngreso)}</div>
      </div>

      <!-- DATOS DEL CLIENTE -->
      <div style="padding:3px 0;border-bottom:1px solid #ddd;margin-bottom:3px;">
        <div style="font-size:10px;font-weight:bold;text-align:center;">DATOS DEL CLIENTE</div>
        <div style="font-size:9px;text-align:center;line-height:1.4;">
          <div>${cliente.nombre}</div>
          <div>${cliente.telefono}</div>
        </div>
      </div>

      <!-- DATOS DEL EQUIPO -->
      <div style="padding:3px 0;border-bottom:1px solid #ddd;margin-bottom:3px;">
        <div style="font-size:10px;font-weight:bold;text-align:center;">DATOS DEL EQUIPO</div>
        <div style="font-size:9px;line-height:1.4;padding:0 10px;">
          <div><span style="font-weight:bold;">EQUIPO:</span> ${equipo}</div>
          <div><span style="font-weight:bold;">PROB:</span> ${problema}</div>
          <div><span style="font-weight:bold;">ACCESORIOS:</span> ${accesorios}</div>
          <div><span style="font-weight:bold;">SEDE:</span> ${sede}</div>
          <div><span style="font-weight:bold;">DIAGNOSTICO:</span> ${diagnostico}</div>
        </div>
      </div>

      <!-- DETALLE -->
      <div style="padding:3px 0;border-bottom:1px solid #ddd;margin-bottom:3px;">
        <div style="font-size:10px;font-weight:bold;text-align:center;">DETALLE</div>
        ${servicios.length > 0 || productos.length > 0 ? `
          <div style="font-size:9px;line-height:1.5;padding:0 10px;">
            ${servicios.map(s => {
              const garantia = s.garantia || s.garantiaDias || 0;
              const precio = s.precio || 0;
              const nombre = (s.nombre || s.nombreServicio || s.observaciones || 'SERVICIO').toUpperCase();
              const garantiaTexto = garantia > 0 ? ` [GARANTÍA: ${formatearGarantiaMostrar(garantia).toUpperCase()}]` : '';
              return `
                <div style="display:flex;justify-content:space-between;padding:1px 0;border-bottom:1px dotted #eee;">
                  <span>${nombre}${garantiaTexto}</span>
                  <span>${formatearMonedaUSD(precio)}</span>
                </div>
              `;
            }).join('')}
            ${productos.map(p => {
              const garantia = p.garantia || p.garantiaDias || 0;
              const precio = p.precio || 0;
              const nombre = (p.nombre || p.nombreProducto || 'PRODUCTO').toUpperCase();
              const garantiaTexto = garantia > 0 ? ` [GARANTÍA: ${formatearGarantiaMostrar(garantia).toUpperCase()}]` : '';
              return `
                <div style="display:flex;justify-content:space-between;padding:1px 0;border-bottom:1px dotted #eee;">
                  <span>${nombre}${garantiaTexto}</span>
                  <span>${formatearMonedaUSD(precio)}</span>
                </div>
              `;
            }).join('')}
          </div>
        ` : `
          <div style="text-align:center;font-size:9px;color:#666;padding:2px 0;">SIN SERVICIOS REGISTRADOS</div>
        `}
      </div>

      <!-- TOTAL - con USD y Bs -->
      <div style="padding:3px 0;border-bottom:1px solid #ddd;margin-bottom:3px;">
        <div style="display:flex;justify-content:space-between;padding:3px 0;font-size:11px;font-weight:bold;border-top:2px solid #000;border-bottom:2px solid #000;">
          <span>TOTAL:</span>
          <span>${formatearMonedaUSD(totalMostrar)}</span>
        </div>
        ${tasaBsUsd > 0 ? `
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;color:#666;">
            <span>TASA Bs/USD:</span>
            <span>${tasaBsUsd.toFixed(2)}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:10px;font-weight:bold;color:#000;">
            <span>TOTAL Bs:</span>
            <span>${formatearMonedaBs(totalBs)}</span>
          </div>
        ` : ''}
        ${montoPagadoRep > 0 ? `
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;border-top:1px solid #ddd;margin-top:2px;padding-top:2px;">
            <span>PAGADO USD:</span>
            <span>${formatearMonedaUSD(montoPagadoRep)}</span>
          </div>
          ${tasaBsUsd > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;">
              <span>PAGADO Bs:</span>
              <span>${formatearMonedaBs(montoPagadoBs)}</span>
            </div>
          ` : ''}
        ` : ''}
      </div>

      <!-- FOOTER -->
      <div style="text-align:center;padding:4px 0 2px 0;margin-top:3px;">
        <div style="font-size:9px;font-weight:bold;">¡GRACIAS POR SU CONFIANZA!</div>
        <div style="font-size:8px;">ENCUÉNTRANOS EN TODAS LAS REDES COMO:</div>
        <div style="font-size:9px;font-weight:bold;color:#cc0000;">${empresa.instagram}</div>
        <div style="font-size:7px;color:#666;">${empresa.slogan}</div>
      </div>
    </div>
  `;
};

// ==================== FUNCIÓN PRINCIPAL - DESCARGAR PDF ====================
export const descargarOrdenServicioPDF = async (orden) => {
  if (!orden) {
    alert('No hay orden para generar PDF');
    return false;
  }

  try {
    // Resetear logo para que se recargue el del tenant actual
    LOGO_BASE64 = '';
    await cargarLogo();

    const datosCliente = await obtenerDatosClienteCompletos(orden);

    const numeroOrden = orden.numeroOrden || orden.NumeroOrden || 'SIN_NUMERO';
    const nombreArchivo = `ORDEN_${numeroOrden}.pdf`;

    const htmlContent = generarHTMLOrdenServicio(orden, datosCliente);

    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.cssText = 'position:absolute;left:-9999px;top:0;width:740px;background:white;padding:0;z-index:-1;';
    document.body.appendChild(container);

    await new Promise(resolve => setTimeout(resolve, 500));

    const canvas = await html2canvas(container, {
      scale: 2.5,
      useCORS: true,
      width: 740,
      height: container.scrollHeight,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = pdfHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
    heightLeft -= pdf.internal.pageSize.getHeight();

    while (heightLeft > 0) {
      position = heightLeft - pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
    }

    pdf.save(nombreArchivo);
    document.body.removeChild(container);

    return true;
  } catch (error) {
    console.error('Error generando PDF:', error);
    alert('Error al generar el PDF: ' + error.message);
    return false;
  }
};

export default {
  EMPRESA_CONFIG,
  cargarLogo,
  getLogoBase64,
  formatearFechaTicket,
  formatearHoraTicket,
  formatearMonedaUSD,
  formatearMonedaBs,
  formatearGarantiaMostrar,
  obtenerDatosClienteCompletos,
  generarHTMLOrdenServicio,
  descargarOrdenServicioPDF
};